import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { Plan } from 'Shared/types/Plan.types';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import * as SubscriptionsActions from 'Shared/actions/Subscriptions.actions';

// Constants
import { billingStates } from 'Shared/constants/constants';

export const useOrganizationSubscription = () => {
  const dispatch = useDispatch();

  const [plans, setPlans] = useState<Plan[]>(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [isFetchingSubscription, toggleIsFetchingSubscription] = useToggle(false);

  const hasOrganization = useSelector(state => !!state?.organization?.id);
  const {
    seatsToBillFor,
    trialExpiredPlanOverride,
    billingState,
    billingCycle,
    trialAlreadyBeenExtended,
    billingVersion,
    blockAllUserAccess,
  } = useSelector(state => state?.organization);

  const isTrial = billingState === billingStates.FREE_TRIAL;
  const isTrialExpired = billingState === billingStates.TRIAL_EXPIRED;
  const isFreePlan = billingState === billingStates.FREE_PLAN;
  const isPaidPlan =
    billingState === billingStates.PAID_PLAN || billingState === billingStates.PAID_PLAN_PREDICTIVEINDEX;

  const isGrowthEligible =
    trialExpiredPlanOverride === 'growth' ||
    (seatsToBillFor >= 25 && trialExpiredPlanOverride !== 'startup' && trialExpiredPlanOverride !== 'free_plan');

  const canSelectBasic =
    trialExpiredPlanOverride === 'free_plan' || (seatsToBillFor < 5 && trialExpiredPlanOverride !== 'growth');

  const canSelectStartup = seatsToBillFor < 25 && trialExpiredPlanOverride !== 'growth';

  const fetchPlanInfo = useCallback(() => {
    const params = {
      pricing_ui_v2: true,
    };
    if (hasOrganization) {
      dispatch(SubscriptionsActions.fetchPlanInfo(params)).then(response => {
        setPlans(response.data.map(plan => plan.attributes));
      });
    }
  }, []);

  const fetchActiveSubscription = useCallback(() => {
    if (hasOrganization) {
      toggleIsFetchingSubscription();
      dispatch(SubscriptionsActions.fetchActiveSubscription()).then(data => {
        setActiveSubscription(data);
        toggleIsFetchingSubscription();
      });
    }
  }, [billingState, billingCycle]);

  return {
    isGrowthEligible,
    billingState,
    trialAlreadyBeenExtended,
    isTrial,
    isPaidPlan,
    trialExpiredPlanOverride,
    isTrialExpired,
    isFreePlan,
    billingVersion,
    canSelectBasic,
    seatsToBillFor,
    canSelectStartup,
    plans,
    activeSubscription,
    fetchPlanInfo,
    fetchActiveSubscription,
    blockAllUserAccess,
    isLoading: isFetchingSubscription || !plans,
  };
};
